import type { EditorScreen } from '@/store/editor/types/screen';
import {
  visitorPathFrom,
  type FunnelGraphResult,
} from '@/logic-engine/funnel-graph';

/**
 * Прогресс «узлы по разделам» (пак `chrome.progress: 'section-nodes'`) —
 * повтор шапки эталона Slimkit, spec shared/header.md «Логика прогресса» и
 * «Что добавить у нас» п.1.
 *
 * Узел = экран с флагом `progressSection` в ПУТИ ПОСЕТИТЕЛЯ (пройденное +
 * остаток по безусловной ступени, тот же путь, что у знаменателя полосы,
 * WEB-1812); первый экран пути — узел всегда. Для соседних узлов A и B:
 *   заливка дорожки = clamp((i − A) / (B − A), 0, 1),
 *   узел отмечен галочкой, если i ≥ позиции узла,
 * где i — позиция текущего экрана в пути (эталон `line/index.tsx:15-21`,
 * `sircle/index.tsx:10-23`). Ветвление пересчитывает путь само собой.
 */

/** Длительность заливки дорожки и задержка галочки, мс (эталон `.3s`, `setTimeout 300`). */
export const SECTION_PROGRESS_MS = 300;

export interface SectionProgressState {
  /** Узлы по порядку: true — пройден/текущий раздел (галочка). */
  nodes: boolean[];
  /** Доли заливки дорожек между соседними узлами, 0..1; длина = узлов − 1. */
  fills: number[];
}

/** Экран пути: флаг начала раздела, подпись раздела, пропуск в счёте. */
export type SectionFlagged =
  | Pick<EditorScreen, 'progressSection' | 'progressSectionLabel' | 'progressSkip'>
  | undefined;

/**
 * stylepack/progress-skip: экраны с `progressSkip: true` в узлы и дорожки не
 * идут — как и в счётчик шапки (quizShownStep.ts). Отдаёт путь из одних
 * засчитанных экранов (флаг раздела пропускаемого экрана переходит на
 * следующий засчитанный — раздел не теряется) и позицию: сколько засчитанных
 * до `current` плюс он сам, если засчитан, минус один; не меньше нуля.
 */
function withoutSkipped(
  path: readonly SectionFlagged[],
  current: number,
): [SectionFlagged[], number] {
  const out: SectionFlagged[] = [];
  // Начало раздела с пропускаемого экрана — флаг и подпись раздела (её
  // берут шаги-разделы шапки, sectionSteps.ts) переходят на следующий
  // засчитанный экран.
  let carried: SectionFlagged | null = null;
  let position = -1;
  for (let i = 0; i < path.length; i += 1) {
    const screen = path[i];
    if (screen?.progressSkip === true) {
      if (screen.progressSection === true) carried = screen;
    } else {
      out.push(
        carried
          ? {
              ...screen,
              progressSection: true,
              // Сам засчитанный экран — тоже начало раздела: подпись его.
              progressSectionLabel:
                screen?.progressSection === true
                  ? screen.progressSectionLabel
                  : carried?.progressSectionLabel,
            }
          : screen,
      );
      carried = null;
    }
    if (i === current) position = out.length - 1;
  }
  return [out, Math.max(0, position)];
}

/**
 * Путь без пропускаемых экранов, если они есть. Шов progress-skip ×
 * section-bars / section-steps (сведение stylepack/integration, 22.10.2026):
 * зовётся в самой двери пути `visitorSectionPath`, а не у одних узлов пака —
 * иначе отрезки полосы по разделам и шаги-разделы шапки считали бы экран,
 * которого нет в счётчике.
 */
function countedPath(
  path: SectionFlagged[],
  current: number,
): { path: SectionFlagged[]; current: number } {
  if (!path.some((s) => s?.progressSkip === true)) return { path, current };
  const [counted, position] = withoutSkipped(path, current);
  return { path: counted, current: position };
}

/** Позиции узлов в пути: 0 всегда, плюс каждый экран с флагом раздела. */
export function sectionNodePositions(
  path: readonly SectionFlagged[],
): number[] {
  if (path.length === 0) return [];
  const positions = [0];
  for (let i = 1; i < path.length; i += 1) {
    if (path[i]?.progressSection === true) positions.push(i);
  }
  return positions;
}

/** Состояние узлов и дорожек для позиции `current` в пути. */
export function sectionProgressState(
  positions: readonly number[],
  current: number,
): SectionProgressState {
  const nodes = positions.map((p) => current >= p);
  const fills: number[] = [];
  for (let k = 0; k + 1 < positions.length; k += 1) {
    const a = positions[k];
    const b = positions[k + 1];
    const steps = b - a;
    const raw = steps > 0 ? (current - a) / steps : 1;
    fills.push(Math.min(1, Math.max(0, raw)));
  }
  return { nodes, fills };
}

export interface VisitorSectionInput {
  screens: readonly EditorScreen[];
  /** Индексы пройденных экранов (стек истории визарда). */
  history: readonly number[];
  graph: FunnelGraphResult;
  currentIndex: number;
  /**
   * Превью конструктора: пути нет (экран выбирает автор) — считаем по списку
   * экранов, как полоса в превью (WEB-916).
   */
  isControlled: boolean;
}

/**
 * Путь посетителя и позиция текущего экрана в нём — одна дверь для узлов пака
 * и шагов-разделов шапки воронки (`sectionSteps.ts`).
 */
export function visitorSectionPath({
  screens,
  history,
  graph,
  currentIndex,
  isControlled,
}: VisitorSectionInput): { path: SectionFlagged[]; current: number } {
  if (isControlled) return countedPath([...screens], currentIndex);
  const current = screens[currentIndex];
  const byId = new Map(screens.map((s) => [s.id, s] as const));
  const ahead = current ? visitorPathFrom(graph, current.id) : [];
  const path: SectionFlagged[] = [
    ...history.map((i) => screens[i]),
    ...(ahead.length > 0 ? ahead.map((id) => byId.get(id)) : [current]),
  ];
  return countedPath(path, history.length);
}

/** Путь посетителя и позиция в нём → состояние узлов по разделам. */
export function sectionProgressForVisitor(
  input: VisitorSectionInput,
): SectionProgressState {
  const { path, current } = visitorSectionPath(input);
  return sectionProgressState(sectionNodePositions(path), current);
}
